import {
  ColumnFactory,
  createDataQualityCellFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  DataQualityAssetType,
  DataQualityCellFormatterRowDataFunctions,
  DataQualityIconHeaderCertificationFormatterFactory,
  DataQualityIconHeaderFormatterFactory,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  mapDC3ColumnFactoriesToVizPortalColumnFactories,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { Formatters } from '@tableau/widgets/lib/gen-src/Localize';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { VirtualConnectionTableOrderField } from '../../../gen-src/queries/globalTypes';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import {
  GlobalAssetGridDC3ColumnFactoryParams,
  makeDC3ColumnFactoryParamsFromVizPortalParams,
} from '../sorting/makeColumnFactoryParams';
import {
  VirtualConnectionTablesValidSortFields,
} from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionTablesDisplayGridRowData } from "../table/types/VirtualConnectionTablesDisplayGridRowData";

const shouldBeObfuscated: (rowData: VirtualConnectionTablesDisplayGridRowData) => boolean = ({
  name,
}) => name === null;

type TablesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<VirtualConnectionTablesValidSortFields>,
  VirtualConnectionTablesDisplayGridRowData,
  VirtualConnectionTablesValidSortFields
>;

const rowDataFunctions: DataQualityCellFormatterRowDataFunctions<VirtualConnectionTablesDisplayGridRowData> = {
  getAssetUrlFromRowData: (urls: SiteUrls, { id }) => urls.virtualConnectionTableUrl(id),
  getNameFromRowData: ({ name }) => name || '',
  getTypeNameFromRowData: (): DataQualityAssetType => 'VirtualConnectionTable',
  getDataQualityCertificationFromRowData: ({ dataQualityCertifications }) => dataQualityCertifications
}


export const columns: ReadonlyArray<TablesColumnFactory> = [
  () => ({
    name: 'certification',
    dataKey: '',
    headerText: '',
    headerTooltip: Messages.Certification(),
    headerFormatter: DataQualityIconHeaderCertificationFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: ({ dataQualityWarnings }) => dataQualityWarnings,
      disableIconOnlyTabIndex: true,
      isCertification: true,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  () => ({
    name: 'warning',
    dataKey: '',
    headerText: '',
    headerTooltip: Messages.DataQualityWarningTitle(),
    headerFormatter: DataQualityIconHeaderFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: ({ dataQualityWarnings }) => dataQualityWarnings,
      disableIconOnlyTabIndex: true,
      isCertification: false,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  (params) => ({
    name: 'name',
    dataKey: VirtualConnectionTableOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(params),
    cellFormatter: createObfuscatedLinkCellFormatter(
      shouldBeObfuscated,
      ({ name }) => name,
      (urls, { id }) => urls.virtualConnectionTableLineagePath(id),
      undefined, // noPadding
      true // disableTabIndex
    ),
    growToFit: true,
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'workbooks',
    dataKey: VirtualConnectionTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
    headerText: Messages.GridHeaderWorkbooks(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'datasources',
    dataKey: VirtualConnectionTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
    headerText: Messages.GridHeaderDatasources(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
    resizable: true,
    sortable: true,
  }),
  (sortParams) => ({
    name: 'extractStatus',
    dataKey: UnsortableColumnKey,
    cellFormatter: createTextCellFormatter(
      ({ isExtracted }) =>
        isExtracted
          ? Messages.ExtractConnection()
          : Messages.LiveConnection()
    ),
    headerText: Messages.GridHeaderConnectionStatus(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    sortable: false,
  }),
];

export const columnsForVizPortal = mapDC3ColumnFactoriesToVizPortalColumnFactories(
  columns,
  makeDC3ColumnFactoryParamsFromVizPortalParams
);

export type VirtualConnectionTablesDisplayGridProps = UsualDisplayGridProps<
  VirtualConnectionTablesDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<VirtualConnectionTablesValidSortFields>;

export function VirtualConnectionTablesDisplayGrid(props: VirtualConnectionTablesDisplayGridProps): JSX.Element {
  const cols = columns.map((columnFactory) => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
